/**
 * 激活码池管理 — pool CRUD + batch distribution
 *
 * Manages per-token activation pools (RP/TP/EP), adjusting pool balances,
 * toggling active status, and batch-distributing activation codes to users.
 */
import { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Tag,
  message,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface PoolRow {
  id: number;
  token: string;
  pool_balance: string;
  total_issued: string;
  is_active: boolean;
  note: string | null;
  create_time: string;
  update_time: string;
}

const TOKEN_OPTIONS = [
  { label: 'RP', value: 'rp' },
  { label: 'TP', value: 'tp' },
  { label: 'EP', value: 'ep' },
];

const TOKEN_COLORS: Record<string, string> = {
  rp: 'blue',
  tp: 'green',
  ep: 'orange',
};

export function ActivationPoolPage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<PoolRow | null>(null);
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [distForm] = Form.useForm();

  // ---- Pool summary stats ---------------------------------------------------
  const { data: poolSummary } = useQuery({
    queryKey: ['admin', 'activation', 'pools', 'summary'],
    queryFn: async () => {
      const res: any = await api.get('/admin/activation/pools', { params: { limit: 100 } });
      return (res.data?.data?.rows ?? []) as PoolRow[];
    },
    refetchInterval: 30_000,
  });

  const totalBalance = poolSummary?.reduce((s, p) => s + Number(p.pool_balance), 0) ?? 0;
  const totalIssued = poolSummary?.reduce((s, p) => s + Number(p.total_issued), 0) ?? 0;
  const activePools = poolSummary?.filter((p) => p.is_active).length ?? 0;

  // ---- Handlers -------------------------------------------------------------

  const handleCreate = async (values: any) => {
    setLoading(true);
    try {
      await api.post('/admin/activation/pools', {
        token: values.token,
        pool_balance: String(values.pool_balance ?? 0),
        is_active: values.is_active ?? true,
        note: values.note || undefined,
      });
      message.success('Pool created');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'activation'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (values: any) => {
    if (!editRow) return;
    setLoading(true);
    try {
      const patch: Record<string, unknown> = {};
      if (values.pool_balance !== undefined) patch.pool_balance = String(values.pool_balance);
      if (values.is_active !== undefined) patch.is_active = values.is_active;
      if (values.note !== undefined) patch.note = values.note || null;
      await api.patch(`/admin/activation/pools/${editRow.id}`, patch);
      message.success('Pool updated');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'activation'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDistribute = async (values: any) => {
    setLoading(true);
    try {
      const userIds = String(values.user_ids)
        .split(/[\s,;]+/)
        .map((s: string) => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter((n: number) => n > 0);
      if (!userIds.length) {
        message.error('Please enter valid user IDs');
        setLoading(false);
        return;
      }
      await api.post('/admin/activation/distribute', {
        token: values.token,
        amount: String(values.amount),
        user_ids: userIds,
        note: values.note || undefined,
      });
      message.success(`Distributed to ${userIds.length} users`);
      setDistributeOpen(false);
      distForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'activation'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- Columns --------------------------------------------------------------

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Token',
      dataIndex: 'token',
      width: 80,
      render: (v: string) => <Tag color={TOKEN_COLORS[v] ?? 'default'}>{v.toUpperCase()}</Tag>,
    },
    { title: 'Pool Balance', dataIndex: 'pool_balance', width: 160 },
    { title: 'Total Issued', dataIndex: 'total_issued', width: 160 },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    { title: 'Note', dataIndex: 'note', ellipsis: true },
    { title: 'Created', dataIndex: 'create_time', width: 180, render: (v: string) => v?.slice(0, 19).replace('T', ' ') },
    ...(hasNode('activation.pool.update')
      ? [
          {
            title: 'Action',
            width: 80,
            render: (_: unknown, row: PoolRow) => (
              <Button
                size="small"
                onClick={() => {
                  setEditRow(row);
                  editForm.setFieldsValue({
                    pool_balance: Number(row.pool_balance),
                    is_active: row.is_active,
                    note: row.note ?? '',
                  });
                }}
              >
                Edit
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small"><Statistic title="Active Pools" value={activePools} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Total Pool Balance" value={totalBalance.toFixed(2)} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Total Issued" value={totalIssued.toFixed(2)} /></Card>
        </Col>
      </Row>

      {/* Toolbar */}
      <Space style={{ marginBottom: 16 }}>
        {hasNode('activation.pool.create') && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create Pool
          </Button>
        )}
        {hasNode('activation.distribute') && (
          <Button onClick={() => setDistributeOpen(true)}>
            Distribute Codes
          </Button>
        )}
      </Space>

      {/* Pool table */}
      <DataTable<PoolRow>
        queryKey={['admin', 'activation', 'pools']}
        endpoint="/admin/activation/pools"
        columns={columns}
        rowKey="id"
      />

      {/* Create modal */}
      <Modal
        title="Create Activation Pool"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={loading}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="token" label="Token" rules={[{ required: true }]}>
            <Select options={TOKEN_OPTIONS} placeholder="Select token" />
          </Form.Item>
          <Form.Item name="pool_balance" label="Initial Pool Balance" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit modal */}
      <Modal
        title={`Edit Pool #${editRow?.id}`}
        open={!!editRow}
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="pool_balance" label="Pool Balance">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Distribute modal */}
      <Modal
        title="Batch Distribute Activation Codes"
        open={distributeOpen}
        onCancel={() => setDistributeOpen(false)}
        onOk={() => distForm.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form form={distForm} layout="vertical" onFinish={handleDistribute}>
          <Form.Item name="token" label="Token Pool" rules={[{ required: true }]}>
            <Select options={TOKEN_OPTIONS} placeholder="Select token" />
          </Form.Item>
          <Form.Item name="amount" label="Amount per User" rules={[{ required: true }]}>
            <InputNumber min={0.000001} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="user_ids"
            label="User IDs (comma or newline separated, max 1000)"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={4} placeholder="1, 2, 3 or one per line" />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
